import { FULL_HD, HD, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { makeThemeColor, PRIMARY } from '@tornado-rdk/theme';
import _memoize from 'lodash/memoize';
import { getFont, FONT } from '@tornado-rdk/typography';
import { getScreenSize, identity, middle } from '../utils/sizes';
import { cacheResolver, makeDefaultStylingContext } from '../utils/style';
import {
  BORDER_COLOR,
  BORDER_RADIUS,
  BORDER_WIDTH,
  INNER_BORDER_COLOR,
  SCALE_FOCUSED,
  FOCUSED_BORDER_ALPHA,
  UNFOCUSED_BORDER_ALPHA,
} from '../utils/focus';
import { TRANSPARENT } from '../utils/colors';
import { sizes, templates } from './bigSquare.constants';

export const screenSize = getScreenSize();

export const defaultContext = makeDefaultStylingContext();

export const colors = {
  background: makeThemeColor(PRIMARY, 0.1),
  text: makeThemeColor(PRIMARY),
};

export const borderWidth = {
  [HD]: 2,
  [FULL_HD]: 4,
};

export const ICON_SIZE = {
  [HD]: 16,
  [FULL_HD]: 24,
};

export const CONTENT_PADDING = {
  [HD]: 12,
  [FULL_HD]: 16,
};

export const IMAGE_SIZE = {
  [HD]: 80,
  [FULL_HD]: 96,
};

export const TITLE_ICON_BOX_HEIGHT = {
  [HD]: 24,
  [FULL_HD]: 30,
};

export const PROGRESS_MARGIN_VERTICAL = 16;
export const TITLE_MARGIN_LEFT = 4;
export const EXTRA_TITLE_MARGIN_TOP = 4;
export const PROGRESS_HEIGHT = 4;

export const getIconViewBox = _memoize(screenSize => ({
  w: ICON_SIZE[screenSize],
  h: ICON_SIZE[screenSize],
}));

export const getContainerSize = _memoize(screenSize => ({
  w: sizes[templates.BigSquare][RENDERING_CONTEXT.GENERAL][screenSize].w - CONTENT_PADDING[screenSize] * 2,
  h: sizes[templates.BigSquare][RENDERING_CONTEXT.GENERAL][screenSize].h - CONTENT_PADDING[screenSize] * 2,
}));

export const getActualTitleLeftOffset = isIconVisible =>
  isIconVisible ? TITLE_MARGIN_LEFT + ICON_SIZE[screenSize] : 0;

export const getTitleWrapWidth = _memoize(
  screenSize => getContainerSize(screenSize).w - getIconViewBox(screenSize).w - TITLE_MARGIN_LEFT,
);

export const getProgressTopOffset = _memoize(screenSize => IMAGE_SIZE[screenSize] + PROGRESS_MARGIN_VERTICAL);

export const getTitleIconBoxTopOffset = _memoize(
  screenSize => IMAGE_SIZE[screenSize] + PROGRESS_HEIGHT + PROGRESS_MARGIN_VERTICAL * 2,
);

export const getExtraTitleTopOffset = _memoize(
  screenSize =>
    IMAGE_SIZE[screenSize] +
    PROGRESS_HEIGHT +
    TITLE_ICON_BOX_HEIGHT[screenSize] +
    EXTRA_TITLE_MARGIN_TOP +
    PROGRESS_MARGIN_VERTICAL * 2,
);

export const getInitialStyle = ({ screenSize, colorMode } = defaultContext) => {
  const { w, h } = sizes[templates.BigSquare][RENDERING_CONTEXT.GENERAL][screenSize];
  const borderSize = BORDER_WIDTH[screenSize];
  const borderOffset = borderSize * 2;
  const containerSize = getContainerSize(screenSize);

  return {
    Border: {
      alpha: 0,
      w: w + borderOffset,
      h: h + borderOffset,
      x: -borderSize,
      y: -borderSize,
      color: TRANSPARENT,
      shader: {
        radius: BORDER_RADIUS[screenSize],
        outline: BORDER_WIDTH[screenSize],
        strokeColor: TRANSPARENT,
        innerColor: TRANSPARENT,
      },
    },
    Background: {
      w,
      h,
      color: TRANSPARENT,
      shader: {
        radius: BORDER_RADIUS[screenSize],
      },
    },
    ContentContainer: {
      w: containerSize.w,
      h: containerSize.h,
      x: CONTENT_PADDING[screenSize],
      y: CONTENT_PADDING[screenSize],
      Logo: {
        w: IMAGE_SIZE[screenSize],
        h: IMAGE_SIZE[screenSize],
      },
      Progress: {
        w: getContainerSize(screenSize).w,
        y: getProgressTopOffset(screenSize),
      },
      TitleIconBox: {
        w: identity,
        h: TITLE_ICON_BOX_HEIGHT[screenSize],
        y: getTitleIconBoxTopOffset(screenSize),
        Icon: {
          mountY: 0.5,
          y: middle,
          alpha: 0.8,
        },
        Title: {
          color: colors.text[colorMode],
          text: {
            ...getFont(FONT.BERLIN_3, screenSize),
            maxLines: 1,
            verticalAlign: 'middle',
            wordWrapWidth: getTitleWrapWidth(screenSize),
          },
        },
      },
      ExtraTitle: {
        y: getExtraTitleTopOffset(screenSize),
        color: colors.text[colorMode],
        text: {
          ...getFont(FONT.MADRID_1, screenSize),
          maxLines: 1,
          verticalAlign: 'middle',
          wordWrapWidth: getContainerSize(screenSize).w,
        },
        alpha: 0.8,
      },
    },
  };
};

export const getFocusedStyle = _memoize(
  ({ colorMode }) => ({
    Border: {
      smooth: {
        alpha: FOCUSED_BORDER_ALPHA,
        scale: SCALE_FOCUSED,
        'shader.strokeColor': BORDER_COLOR[colorMode],
        'shader.innerColor': INNER_BORDER_COLOR[colorMode],
        'shader.scale': SCALE_FOCUSED,
      },
    },
    Background: {
      smooth: {
        color: colors.background[colorMode],
        scale: SCALE_FOCUSED,
      },
    },
  }),
  cacheResolver,
);

export const unFocusedStyle = {
  Border: {
    smooth: {
      alpha: UNFOCUSED_BORDER_ALPHA,
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  Background: {
    smooth: {
      color: TRANSPARENT,
      scale: 1,
      'shader.scale': 1.0,
    },
  },
};
